import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { getImage } from "gatsby-plugin-image";
import Seo from "../components/Seo";

import Layout from "../components/Layout";

import FullWidthImage from "../components/FullWidthImage";
import PreviewCompatibleImage from "../components/PreviewCompatibleImage";
import DisplayContentHeader from "../components/DisplayContentHeader";
import BlogList from "../components/BlogList";
import Pagination from "../components/Pagination";

// eslint-disable-next-line
export const AuthorPostTemplate = ({
  title,
  subtitle,
  image,
  meta,
  author,
  posts,
  authors,
  pageContext,
  helmet,
}) => {
  const heroImage = getImage(image) || image;

  const showTitle = meta.showTitle;

  return (
    <div>
      {helmet || null}

      {showTitle ? (
        <FullWidthImage
          img={heroImage}
          title={title}
          subheading={subtitle}
          imgPosition="center"
        />
      ) : (
        <FullWidthImage img={heroImage} imgPosition="center" />
      )}

      <section className="section section--gradient">
        <div className="container">
          <div className="content">
            <div className="content">
              <DisplayContentHeader
                title={author.frontmatter.name}
                parent="Authors"
                parentSlug="/authors"
                date={author.frontmatter.meta.creationDate}
              />
            </div>
            <PreviewCompatibleImage
              imageInfo={{
                image: author.frontmatter.image,
                alt: author.frontmatter.name,
                className: "author-image float-right",
              }}
            />
            <p>{author.frontmatter.summary}</p>
            {posts && posts.length ? (
              <>
                <BlogList
                  posts={posts}
                  authors={authors}
                  title="Blog Entries"
                  emptyMessage="This author has not yet contributed."
                />
                <br className="clear" />
                <Pagination pageContext={pageContext} />
              </>
            ) : (
              <>
                <div>This author has not yet contributed.</div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

AuthorPostTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  meta: PropTypes.shape({
    showTitle: PropTypes.bool,
  }),
  body: PropTypes.string,
  author: PropTypes.object,
  helmet: PropTypes.object,
};

const AuthorPost = ({ data, pageContext }) => {
  let { markdownRemark: post } = data;
  let { allMarkdownRemark: posts } = data;
  let { author } = data;
  let { authors } = data;

  let imageSrc =
    post.frontmatter.image.childImageSharp.gatsbyImageData.images.fallback.src;

  authors = authors.nodes;
  posts = posts.edges;

  return (
    <Layout>
      <AuthorPostTemplate
        title={post.frontmatter.title}
        subtitle={post.frontmatter.subtitle}
        image={post.frontmatter.image}
        meta={post.frontmatter.meta}
        body={post.html}
        author={author}
        posts={posts}
        authors={authors}
        pageContext={pageContext}
        helmet={
          <Seo
            title={post.frontmatter.title}
            summary={post.frontmatter.summary}
            image={imageSrc}
            article={false}
          />
        }
      />
    </Layout>
  );
};

AuthorPost.propTypes = {
  data: PropTypes.object.isRequired,
};

export default AuthorPost;

export const authorPostQuery = graphql`
  query AuthorPost($id: String!, $name: String!, $skip: Int!, $limit: Int!) {
    author: markdownRemark(id: { eq: $id }) {
      html
      fields {
        slug
      }
      frontmatter {
        name
        image {
          childImageSharp {
            gatsbyImageData(
              quality: 100
              layout: FULL_WIDTH
              placeholder: BLURRED
            )
          }
        }
        meta {
          showTitle
          creationDate(formatString: "MMMM Do, YYYY")
        }
        summary
      }
    }
    authors: allMarkdownRemark(
      filter: { frontmatter: { templateKey: { eq: "author-post" } } }
    ) {
      nodes {
        id
        fields {
          slug
        }
        frontmatter {
          name
        }
      }
    }
    markdownRemark(frontmatter: { templateKey: { eq: "author-page" } }) {
      html
      frontmatter {
        title
        subtitle
        image {
          childImageSharp {
            gatsbyImageData(
              quality: 100
              layout: FULL_WIDTH
              placeholder: BLURRED
            )
          }
        }
        meta {
          showTitle
        }
        summary
      }
    }
    allMarkdownRemark(
      sort: { frontmatter: { meta: { creationDate: DESC } } }
      filter: {
        frontmatter: {
          templateKey: { eq: "blog-post" }
          meta: { author: { eq: $name } }
        }
      }
      skip: $skip
      limit: $limit
    ) {
      edges {
        node {
          excerpt(pruneLength: 400)
          id
          fields {
            slug
          }
          frontmatter {
            title
            templateKey
            meta {
              creationDate(formatString: "MMMM Do, YYYY")
              sortDate: creationDate(formatString: "MMMM DD, YYYY")
              showTitle
              author
            }
            summary
            featured
          }
        }
      }
    }
  }
`;
